
import Vue, { PropType } from 'vue'
import SpinnerLoader from '@root/src/components/SpinnerLoader.vue'
import LineChart from '@root/src/components/charts/LineChart'
import SameDayRecPromoBanner from '@/components/chisai/GCB2/SameDayRecPromoBanner.vue'
import { numSort, floatNumSort } from '@root/src/utils'
import { formatDate, formatNumber } from './opz2'

const MONTH_LABELS = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
]

// Первые 7 — цвета радуги, дальше базовая палитра.
const YEAR_COLORS = [
    '#fc0303',
    '#fc9403',
    '#fce303',
    '#6ffc03',
    '#03fcdb',
    '#4B0082',
    '#8B00FF',
    '#4A6CF7',
    '#10B981',
    '#F97316',
    '#EC4899',
    '#F59E0B',
]

export default Vue.extend({
    name: 'Opz2DaysTab',
    components: { SpinnerLoader, LineChart, SameDayRecPromoBanner },
    props: {
        projectId: { type: String, required: true },
        loading: { type: Boolean, default: false },
        dashboardData: { type: Object as PropType<any>, default: () => ({}) },
    },
    data: () => ({
        headers: [
            { text: 'День', value: 'visitDate', align: 'center' },
            { text: 'Количество клиентов', value: 'clientCount', sort: numSort, align: 'center' },
            {
                text: 'Клиентов перезаписано',
                value: 'recordedOtherDay',
                sort: numSort,
                align: 'center',
            },
            {
                text: 'Процент перезаписи',
                value: 'recordedOtherDayProportion',
                sort: floatNumSort,
                align: 'center',
            },
        ],
        footerProps: { 'items-per-page-options': [10, 25, 50] },
        chartOptions: {
            legend: { labels: { usePointStyle: true } },
            plugins: { datalabels: false },
            scales: {
                xAxes: [{ type: 'category' }],
                yAxes: [{ ticks: { callback: (value: number) => `${value}%` } }],
            },
            tooltips: {
                callbacks: {
                    title: (tooltipItems: any, data: any) =>
                        data.datasets[tooltipItems[0].datasetIndex].data[tooltipItems[0].index].x,
                },
            },
            maintainAspectRatio: false,
        } as any,
        chartStyles: { height: '300px', position: 'relative' },
    }),
    computed: {
        /** Средний процент перезаписи за период — порог для окраски (pct hi/lo в макете). */
        avgProportion(): number {
            return Number(
                this.dashboardData?.totalStatData?.total_recorded_other_day_proportion ?? 0
            )
        },
        rows(): any[] {
            return (this.dashboardData?.statByDateData ?? []).map((el: any) => {
                const value = Number(el.recorded_other_day_proportion ?? 0)
                const avg = this.avgProportion
                return {
                    visitDate: formatDate(el.visit_date),
                    clientCount: formatNumber(el.client_count),
                    recordedOtherDay: formatNumber(el.recorded_other_day),
                    recordedOtherDayProportion: `${formatNumber(el.recorded_other_day_proportion)}%`,
                    pctTone: avg <= 0 ? 'base' : value > avg ? 'hi' : value < avg / 2 ? 'lo' : 'base',
                }
            })
        },
        totalRow(): any {
            const total = this.dashboardData?.totalStatData
            if (!total) return null
            return {
                visitDate: 'ИТОГ ЗА ПЕРИОД',
                clientCount: formatNumber(total.total_client_count),
                recordedOtherDay: formatNumber(total.total_recorded_other_day),
                recordedOtherDayProportion: `${formatNumber(
                    total.total_recorded_other_day_proportion
                )}%`,
            }
        },
        chartData(): any {
            const src =
                this.dashboardData?.totalGraphData?.total_recorded_other_day_proportion || []
            if (!Array.isArray(src) || !src.length) {
                return { labels: [], datasets: [] }
            }

            const byYear: Record<string, any[]> = {}
            src.forEach((item: any) => {
                const year = String(item.year)
                if (!byYear[year]) byYear[year] = []
                byYear[year].push(item)
            })

            const datasets = Object.keys(byYear)
                .sort()
                .map((yearKey, idx) => {
                    const items = byYear[yearKey]
                    const data = MONTH_LABELS.map((_, monthIndex) => {
                        const found = items.find((el: any) => el.month === monthIndex + 1)
                        const value = found?.total_recorded_other_day_proportion
                        return value == null ? null : Math.round(value)
                    })
                    return {
                        label: yearKey,
                        data,
                        borderColor: YEAR_COLORS[idx % YEAR_COLORS.length],
                        backgroundColor: 'transparent',
                        fill: false,
                        lineTension: 0.2,
                        pointRadius: 3,
                    }
                })

            return { labels: MONTH_LABELS, datasets }
        },
    },
})
